import type { GanttTask } from '../types/gantt';
import type { Offer } from './offers';
import type { PublicPlan } from './publicPlans';
import * as projectsAPI from '../services/projects';
import type { ProjectResponse } from '../services/projects';

export type Project = {
	id: string;
	name: string;
	client: string;
	seekerName?: string;
	seekerEmail?: string;
	status: 'planning' | 'quoted' | 'in_progress' | 'completed' | 'cancelled';
	budget: number;
	dueDate?: string;
	owner: string;
	priority: 'low' | 'medium' | 'high';
	tags: string[];
	updatedAt: string;
	ganttData?: GanttTask[]; // planned timeline from offer
	actualProgress?: Array<{ taskId: string; progress: number; status: string }>; // actual progress updates
	// New fields for offer-based projects
	offerId?: string; // Link back to original offer
	planId?: string; // Link back to original plan
	gardenerIds?: string[]; // Array of gardener IDs assigned to this project
	gardenerName?: string; // Name of the gardener/company that sent the offer
	/** Company context for this project (from assigned gardeners), for multi-company UI */
	companyName?: string;
	companyId?: string;
};

/**
 * Compute a human-friendly project label for display in lists, tasks, schedule, etc.
 * Example: "Ivana Horvat – Backyard Mediterranean"
 */
export function getProjectDisplayName(project: Project): string {
	const rawName = project.name && project.name.trim().length > 0 ? project.name.trim() : 'Untitled project';
	const client = project.client && project.client.trim().length > 0 ? project.client.trim() : '';
	const seekerName = project.seekerName && project.seekerName.trim().length > 0 ? project.seekerName.trim() : '';
	// Treat plain emails as "not a proper name" for display purposes
	const looksLikeEmail = client.includes('@');

	// Include first tag (often the style) as a suffix when available
	const primaryTag = Array.isArray(project.tags) && project.tags.length > 0
		? String(project.tags[0]).trim()
		: '';

	let label = rawName;

	// Prefer seeker name; otherwise use client when it looks like a name (not an email)
	const namePart = seekerName || (client && !looksLikeEmail ? client : '');
	if (namePart) {
		label = `${namePart} – ${rawName}`;
	}

	// Suffix with primary tag/style if it adds information
	if (primaryTag && !rawName.toLowerCase().includes(primaryTag.toLowerCase())) {
		label = `${label} – ${primaryTag}`;
	}

	return label;
}

// Map backend response to frontend format
function mapBackendProjectToFrontend(backendProject: ProjectResponse): Project {
	// Ensure budget is a number
	const budget = typeof backendProject.budget === 'string' 
		? parseFloat(backendProject.budget) 
		: typeof backendProject.budget === 'number' 
			? backendProject.budget 
			: 0;
	
	// Ensure gardener_ids is an array and convert to strings
	const gardenerIds = Array.isArray(backendProject.gardener_ids)
		? backendProject.gardener_ids.map(id => String(id))
		: backendProject.gardener_ids
			? [String(backendProject.gardener_ids)]
			: [];
	
	return {
		id: String(backendProject.id),
		name: backendProject.name || 'Untitled Project',
		client: backendProject.client || '',
		seekerName: backendProject.seeker_name || undefined,
		seekerEmail: backendProject.seeker_email || undefined,
		status: backendProject.status === 'cancelled' ? 'completed' : (backendProject.status || 'planning'),
		budget: budget,
		dueDate: backendProject.due_date || undefined,
		owner: backendProject.gardener_names?.[0] || backendProject.client || 'Unknown',
		priority: backendProject.priority || 'medium',
		tags: Array.isArray(backendProject.tags) ? backendProject.tags : [],
		updatedAt: backendProject.updated_at || new Date().toISOString(),
		ganttData: Array.isArray(backendProject.gantt_data) ? backendProject.gantt_data : [],
		offerId: backendProject.offer_id ? String(backendProject.offer_id) : undefined,
		planId: backendProject.plan_id ? String(backendProject.plan_id) : undefined,
		gardenerIds: gardenerIds,
		gardenerName: backendProject.gardener_names?.[0] || undefined,
		companyName: backendProject.project_company_name?.trim() || undefined,
		companyId:
			backendProject.project_company_id != null && String(backendProject.project_company_id).trim() !== ''
				? String(backendProject.project_company_id)
				: undefined
	};
}

// Load projects from localStorage
function loadProjects(): Project[] {
	const stored = localStorage.getItem('projects');
	if (stored) {
		try {
			const parsed = JSON.parse(stored) as Project[];
			return parsed;
		} catch {
			// Ignore and use defaults
		}
	}

	// Default demo projects
	return [
		{
			id: 'pr1',
			name: 'Backyard Modern',
			client: 'Alice',
			status: 'planning',
			budget: 1500,
			dueDate: new Date(Date.now() + 86400000 * 30).toISOString(),
			owner: 'Marta',
			priority: 'medium',
			tags: ['residential', 'modern', 'outdoor-living'],
			updatedAt: new Date(Date.now() - 86400000 * 3).toISOString()
		},
		{
			id: 'pr2',
			name: 'Cottage Entrance',
			client: 'Bob',
			status: 'quoted',
			budget: 1100,
			owner: 'Jonas',
			priority: 'low',
			tags: ['residential', 'front-yard'],
			updatedAt: new Date(Date.now() - 86400000 * 6).toISOString()
		},
		{
			id: 'pr3',
			name: 'Zen Corner',
			client: 'Clara',
			status: 'in_progress',
			budget: 2200,
			dueDate: new Date(Date.now() + 86400000 * 45).toISOString(),
			owner: 'Marta',
			priority: 'high',
			tags: ['wellness', 'water-feature', 'hardscape'],
			updatedAt: new Date(Date.now() - 86400000).toISOString()
		},
		{
			id: 'pr4',
			name: 'Rooftop Oasis',
			client: 'Dana',
			status: 'completed',
			budget: 3200,
			dueDate: new Date(Date.now() - 86400000 * 10).toISOString(),
			owner: 'Nikhil',
			priority: 'high',
			tags: ['commercial', 'sustainable', 'irrigation'],
			updatedAt: new Date(Date.now() - 86400000 * 12).toISOString()
		},
		{
			id: 'pr5',
			name: 'Community Courtyard',
			client: 'Eco Collective',
			status: 'completed',
			budget: 5400,
			dueDate: new Date(Date.now() - 86400000 * 35).toISOString(),
			owner: 'Jonas',
			priority: 'medium',
			tags: ['community', 'native-plants'],
			updatedAt: new Date(Date.now() - 86400000 * 20).toISOString()
		}
	];
}

// Exported projects variable (for backward compatibility)
// This is initialized from localStorage and can be updated by async getProjects()
export let projects: Project[] = loadProjects();

// Update the exported projects variable when async data loads
// This keeps backward compatibility for components that use `projects` directly
export function updateProjectsCache(newProjects: Project[]) {
	projects = newProjects;
	// Also save to localStorage for persistence
	try {
		localStorage.setItem('projects', JSON.stringify(projects));
		window.dispatchEvent(new CustomEvent('projectsUpdated'));
	} catch (err) {
		console.error('Failed to save projects to localStorage', err);
	}
}

// Save projects to localStorage
function saveProjects() {
	try {
		localStorage.setItem('projects', JSON.stringify(projects));
		window.dispatchEvent(new CustomEvent('projectsUpdated'));
	} catch (err) {
		console.error('Failed to save projects to localStorage', err);
	}
}

// Get all projects (tries backend first, falls back to localStorage)
export async function getProjects(
	arg?: number | { user_id?: number; seeker_user_id?: number; status?: string; requester_user_id?: number; company_scope?: 'company' | 'all_companies' }
): Promise<Project[]> {
	try {
		const params =
			typeof arg === 'number'
				? ({ user_id: arg } as any)
				: arg;
		const backendProjects = await projectsAPI.getProjects(params);
		const mappedProjects = backendProjects.map(mapBackendProjectToFrontend);
		// Update the exported projects variable for backward compatibility
		updateProjectsCache(mappedProjects);
		return mappedProjects;
	} catch (err) {
		console.error('Failed to load projects from API, falling back to localStorage:', err);
		projects = loadProjects();
		return projects;
	}
}

/** Get projects where the current user is the plan owner (seeker). For "Current Projects" in My Plans. */
export async function getProjectsForSeeker(seekerUserId: number): Promise<Project[]> {
	try {
		const backendProjects = await projectsAPI.getProjects({ seeker_user_id: seekerUserId });
		return backendProjects.map(mapBackendProjectToFrontend);
	} catch (err) {
		console.error('Failed to load seeker projects from API:', err);
		return [];
	}
}

// Get project by ID (tries backend first, falls back to localStorage)
export async function getProjectById(projectId: string): Promise<Project | undefined> {
	// Check if it's a numeric ID (backend) or string ID (localStorage)
	const projectIdNum = parseInt(projectId, 10);
	if (!isNaN(projectIdNum)) {
		try {
			const backendProject = await projectsAPI.getProjectById(projectIdNum);
			return mapBackendProjectToFrontend(backendProject);
		} catch (err) {
			console.error('Failed to load project from API, falling back to localStorage:', err);
		}
	}
	return projects.find(p => p.id === projectId);
}

// Create project from accepted offer (tries backend first, falls back to localStorage)
export async function createProjectFromOffer(offer: Offer, plan: PublicPlan): Promise<Project> {
	const timelineSummary = offer.timelineSummary;
	
	// Calculate due date from timeline if available
	let dueDate: string | undefined;
	if (timelineSummary?.endDate) {
		dueDate = timelineSummary.endDate.split('T')[0]; // Just the date part
	} else if (offer.timelineEstimate) {
		// Try to parse timeline estimate (e.g., "4-5 weeks")
		const weeksMatch = offer.timelineEstimate.match(/(\d+)[-\s](\d+)?\s*week/i);
		if (weeksMatch) {
			const weeks = parseInt(weeksMatch[2] || weeksMatch[1], 10);
			const due = new Date();
			due.setDate(due.getDate() + (weeks * 7));
			dueDate = due.toISOString().split('T')[0];
		}
	}
	
	const offerIdNum = parseInt(offer.id, 10);
	const planIdNum = plan.databaseId || (plan.id ? parseInt(plan.id, 10) : undefined);
	const gardenerIdNum = parseInt(offer.gardenerId, 10);
	
	// Try backend first (if offer was already accepted, backend returns existing project with tasks + gardener assignments)
	if (!isNaN(offerIdNum) && !isNaN(gardenerIdNum)) {
		try {
			const backendProject = await projectsAPI.createProject({
				offer_id: offerIdNum,
				plan_id: !isNaN(planIdNum || NaN) ? planIdNum : undefined,
				name: plan.title || 'Garden Project',
				client: plan.contactEmail || 'Client',
				status: 'planning',
				budget: offer.bands.mid,
				due_date: dueDate,
				priority: 'medium',
				tags: [plan.styleKey || 'modern'],
				gantt_data: offer.timelineData ?? offer.ganttData ?? [],
				gardener_ids: [gardenerIdNum]
			});
			return mapBackendProjectToFrontend(backendProject);
		} catch (err) {
			console.error('Failed to create project in backend, falling back to localStorage:', err);
		}
	}
	
	// Fallback to localStorage
	const projectId = `pr-${offer.id}`;
	const project: Project = {
		id: projectId,
		name: plan.title || 'Garden Project',
		client: plan.contactEmail || 'Client',
		status: 'planning',
		budget: offer.bands.mid,
		dueDate: dueDate ? new Date(dueDate).toISOString() : undefined,
		owner: offer.gardenerName,
		priority: 'medium',
		tags: [plan.styleKey || 'modern'],
		updatedAt: new Date().toISOString(),
		ganttData: offer.timelineData ?? offer.ganttData ?? [],
		offerId: offer.id,
		planId: plan.id,
		gardenerIds: [offer.gardenerId],
		gardenerName: offer.gardenerName
	};
	
	const existingIndex = projects.findIndex(p => p.id === projectId || p.offerId === offer.id);
	if (existingIndex >= 0) {
		projects[existingIndex] = project;
	} else {
		projects.push(project);
	}
	
	saveProjects();
	return project;
}

// Add gardener to project (tries backend first, falls back to localStorage)
export async function addGardenerToProject(projectId: string, gardenerId: string): Promise<void> {
	const projectIdNum = parseInt(projectId, 10);
	const gardenerIdNum = parseInt(gardenerId, 10);
	
	if (!isNaN(projectIdNum) && !isNaN(gardenerIdNum)) {
		try {
			await projectsAPI.assignGardenerToProject(projectIdNum, gardenerIdNum);
			return;
		} catch (err) {
			console.error('Failed to assign gardener in backend, falling back to localStorage:', err);
		}
	}
	
	// Fallback to localStorage
	const project = projects.find(p => p.id === projectId);
	if (!project) return;
	
	if (!project.gardenerIds) {
		project.gardenerIds = [];
	}
	
	if (!project.gardenerIds.includes(gardenerId)) {
		project.gardenerIds.push(gardenerId);
		project.updatedAt = new Date().toISOString();
		saveProjects();
	}
}

// Remove gardener from project (tries backend first, falls back to localStorage)
export async function removeGardenerFromProject(projectId: string, gardenerId: string): Promise<void> {
	const projectIdNum = parseInt(projectId, 10);
	const gardenerIdNum = parseInt(gardenerId, 10);
	
	if (!isNaN(projectIdNum) && !isNaN(gardenerIdNum)) {
		try {
			await projectsAPI.removeGardenerFromProject(projectIdNum, gardenerIdNum);
			return;
		} catch (err) {
			console.error('Failed to remove gardener in backend, falling back to localStorage:', err);
		}
	}
	
	// Fallback to localStorage
	const project = projects.find(p => p.id === projectId);
	if (!project || !project.gardenerIds) return;
	
	project.gardenerIds = project.gardenerIds.filter(id => id !== gardenerId);
	project.updatedAt = new Date().toISOString();
	saveProjects();
}

// Update project (tries backend first, falls back to localStorage)
export async function updateProject(projectId: string, updates: Partial<Project>): Promise<void> {
	const projectIdNum = parseInt(projectId, 10);
	
	if (!isNaN(projectIdNum)) {
		try {
			const backendUpdates: any = {};
			if (updates.name) backendUpdates.name = updates.name;
			if (updates.client) backendUpdates.client = updates.client;
			if (updates.status) backendUpdates.status = updates.status;
			if (updates.budget !== undefined) backendUpdates.budget = updates.budget;
			if (updates.dueDate) backendUpdates.due_date = updates.dueDate.split('T')[0];
			if (updates.priority) backendUpdates.priority = updates.priority;
			if (updates.tags) backendUpdates.tags = updates.tags;
			if (updates.ganttData) backendUpdates.gantt_data = updates.ganttData;
			
			await projectsAPI.updateProject(projectIdNum, backendUpdates);
			return;
		} catch (err) {
			console.error('Failed to update project in backend, falling back to localStorage:', err);
		}
	}
	
	// Fallback to localStorage
	const index = projects.findIndex(p => p.id === projectId);
	if (index === -1) return;
	
	projects[index] = {
		...projects[index],
		...updates,
		updatedAt: new Date().toISOString()
	};
	saveProjects();
}


